import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useTheme } from "../../theme/useTheme";
import { SelectedPeriodProvider } from "../../hooks/useSelectedPeriod";
import { AdminNav } from "./AdminNav";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { PageBackground } from "./PageBackground";
import styles from "./AppShell.module.css";

/**
 * The application shell every screen renders inside: a skip link, the header (brand, theme
 * toggle, signed-in indicator/sign-out), a primary top tab bar once signed in, and a single main
 * region. The admin section can surface a secondary row of links as a focused sub-navigation
 * without introducing a side menu or nested chrome elsewhere.
 *
 * @param props.children - The active route's content, rendered inside the main landmark.
 */
export function AppShell({ children }: { children: ReactNode }): React.JSX.Element {
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const [iconsInFooter, setIconsInFooter] = useState(false);
  const pathname = useLocation().pathname;
  const isAdminRoute = pathname.startsWith("/admin/");
  const isAdminUserDetailRoute = /^\/admin\/users\/[^/]+$/.test(pathname);
  const isAdminUsersRoute = pathname === "/admin/users" || pathname === "/admin/requests" || isAdminUserDetailRoute;
  // The REST API reference is a three-column reference document, and My Requests is a wide table of expandable rows, so both take the full width of the main region.
  const isWideRoute = pathname === "/rest-api" || pathname.startsWith("/rest-api/") || pathname === "/my-requests";
  // My Requests fills the main region: the page never scrolls, and only the table's body does.
  const isFillRoute = pathname === "/my-requests";

  return (
    <SelectedPeriodProvider>
      <div className={styles.shell}>
      <PageBackground theme={theme} />
      <a className="skip-link" href="#main-content">
        {t("app.skipToContent")}
      </a>
      <Header theme={theme} onToggleTheme={toggleTheme} iconsInFooter={iconsInFooter} onIconsInFooterChange={setIconsInFooter} />
      <AdminNav />
      <main id="main-content" className={`${styles.main} ${isAdminUsersRoute ? styles.usersMain : ""} ${isAdminUserDetailRoute ? styles.usersDetailMain : ""} ${isFillRoute ? styles.fillMain : ""}`} tabIndex={-1}>
        <div className={`${styles.content} ${isAdminRoute ? styles.adminContent : ""} ${isAdminUsersRoute ? styles.usersContent : ""} ${isAdminUserDetailRoute ? styles.usersDetailContent : ""} ${isWideRoute ? styles.wideContent : ""} ${isFillRoute ? styles.fillContent : ""}`}>{children}</div>
      </main>
      <Footer theme={theme} onToggleTheme={toggleTheme} iconsInFooter={iconsInFooter} />
      </div>
    </SelectedPeriodProvider>
  );
}
